import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Checkbox,
} from '@mui/material';
import { CalendarEventDto } from '../../../types/calendar';
import { CreateFluidEventDto, UpdateFluidEventDto } from '../../../services/calendar.service';
import { ModalIndices } from '../../../common/i-doc/i-doc.registry.ts';
import { localNowIso, toLocalIso } from '@/utils/datetime';

// --- Interfaces ---
// Пропси компонента
interface AddLiquidEventModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateFluidEventDto | (UpdateFluidEventDto & { id: number })) => void;
  eventToEdit?: CalendarEventDto | null;
  calendarId?: number;
  initialScheduledAt?: Date;
}

const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

export const AddLiquidEventModal: React.FC<AddLiquidEventModalProps> = ({ open, onClose, onSave, eventToEdit, calendarId, initialScheduledAt }) => {
  // --- State Management ---
  const [scheduledAt, setScheduledAt] = useState(localNowIso().slice(0, 16));
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const isEditMode = !!eventToEdit;

  // Заповнюємо форму даними, якщо це режим редагування
  useEffect(() => {
    if (open) {
      const now = new Date();
      if (isEditMode && eventToEdit) {
        const eventDate = new Date(eventToEdit.scheduledLdt || now);
        setScheduledAt(toLocalIso(eventDate).slice(0, 16));
        setAmount(eventToEdit.details?.qty?.toString() || '');
        setShowDateTimePicker(eventDate > now);
        setIsCompleted(eventToEdit.isCompleted ?? false);
        setError(null);
      } else {
        // Скидаємо стан для режиму створення
        const initDate = initialScheduledAt ?? now;
        setScheduledAt(toLocalIso(initDate).slice(0, 16));
        setAmount('');
        setShowDateTimePicker(Boolean(initialScheduledAt));
        setIsCompleted(false);
        setError(null);
      }
    }
  }, [open, isEditMode, eventToEdit]);

  // --- Logic ---
  const handleSave = () => {
    const amountNumber = parseInt(amount, 10);
    if (!amount || isNaN(amountNumber) || amountNumber <= 0) {
      setError('Вкажіть коректну кількість');
      return;
    }

    const finalScheduledAt = showDateTimePicker ? new Date(scheduledAt) : new Date();
    const finalIsCompleted = showDateTimePicker ? isCompleted : true;

    const commonData = {
      qty: amountNumber,
      scheduledLdt: toLocalIso(finalScheduledAt),
      isCompleted: finalIsCompleted,
    };

    if (isEditMode && eventToEdit) {
      // Режим редагування
      const eventData: UpdateFluidEventDto & { id: number } = {
        id: eventToEdit.id,
        ...commonData,
      };
      onSave(eventData);
    } else {
      // Режим створення
      const eventData: CreateFluidEventDto = {
        ...commonData,
        calendarId: calendarId,
      };
      onSave(eventData);
    }
  };

  return (
    <Modal open={open} onClose={onClose} data-i-doc={ModalIndices.ADD_LIQUID_EVENT}>
      <Box sx={modalStyle}>
        <Typography variant="h6" component="h2">
          {isEditMode ? 'Редагувати рідину' : 'Додати рідину'}
        </Typography>
        
        <TextField
          label="Кількість (мл)"
          type="number"
          value={amount}
          onChange={(e) => {
            if (error) setError(null);
            setAmount(e.target.value);
          }}
          fullWidth
          margin="normal"
          error={!!error}
          helperText={error}
          autoFocus
        />

        <FormControlLabel
          control={
            <Switch
              checked={showDateTimePicker}
              onChange={(e) => setShowDateTimePicker(e.target.checked)}
              name="scheduleEvent"
              color="primary"
            />
          }
          label="Запланувати на інший час"
          sx={{ mt: 1, mb: 1 }}
        />

        {showDateTimePicker && (
          <TextField
            label="Час"
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        )}

        {showDateTimePicker && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isCompleted}
                onChange={(e) => setIsCompleted(e.target.checked)}
              />
            }
            label="Позначити як виконане"
          />
        )}

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} sx={{ mr: 1 }}>
            Скасувати
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave}>
            {isEditMode ? 'Зберегти зміни' : 'Зберегти'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
